import { useState, useCallback, useRef } from 'react';
import { z } from 'zod';
import { validateForm } from '@/lib/validation';

interface UseFormOptions<T> {
  schema: z.ZodSchema<T>;
  initialValues: T;
  onSubmit: (values: T) => Promise<void> | void;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
}

export function useForm<T extends Record<string, unknown>>({
  schema,
  initialValues,
  onSubmit,
  validateOnChange = false,
  validateOnBlur = true,
}: UseFormOptions<T>) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Validate all fields
  const validateAll = useCallback((): boolean => {
    const result = validateForm(schema, values);
    
    if (result.success === false) {
      setErrors(result.errors);
      return false;
    }
    
    setErrors({});
    return true;
  }, [schema, values]);

  // Validate a single field using the full schema
  const validateField = useCallback(
    (name: keyof T, value: unknown) => {
      // Create a partial schema for just this field
      const testData = { ...values, [name]: value };
      const result = validateForm(schema, testData);
      
      if (result.success === false) {
        const fieldError = result.errors[name as string];
        if (fieldError) {
          setErrors((prev) => ({ ...prev, [name]: fieldError }));
          return false;
        }
      }
      
      // Clear error if field is now valid
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name as string];
        return newErrors;
      });
      return true;
    },
    [schema, values]
  );

  // Handle field change with debounced validation
  const handleChange = useCallback(
    (name: keyof T, value: unknown) => {
      setValues((prev) => ({ ...prev, [name]: value }));
      
      if (validateOnChange && touched[name as string]) {
        // Debounce validation
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        
        debounceTimerRef.current = setTimeout(() => {
          validateField(name, value);
        }, 300);
      }
    },
    [validateOnChange, touched, validateField]
  );

  // Handle field blur
  const handleBlur = useCallback(
    (name: keyof T) => {
      setTouched((prev) => ({ ...prev, [name]: true }));
      
      if (validateOnBlur) {
        validateField(name, values[name]);
      }
    },
    [validateOnBlur, validateField, values]
  );

  // Handle form submission
  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      
      // Mark all fields as touched
      const allTouched = Object.keys(values).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      );
      setTouched(allTouched);
      
      // Validate all fields
      const isValid = validateAll();
      if (!isValid) {
        return;
      }
      
      setIsSubmitting(true);
      
      try {
        await onSubmit(values);
      } finally {
        setIsSubmitting(false);
      }
    },
    [validateAll, onSubmit, values]
  );

  // Reset form to initial values
  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  // Set a specific field value programmatically
  const setFieldValue = useCallback((name: keyof T, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Set multiple field values programmatically
  const setFieldValues = useCallback((newValues: Partial<T>) => {
    setValues((prev) => ({ ...prev, ...newValues }));
  }, []);

  // Clear a specific error
  const clearError = useCallback((name: keyof T) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name as string];
      return newErrors;
    });
  }, []);

  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const isValid = Object.keys(errors).length === 0;

  return {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    handleChange,
    handleBlur,
    handleSubmit,
    reset,
    setFieldValue,
    setFieldValues,
    clearError,
    clearErrors,
    validateAll,
  };
}

export default useForm;
